import React, { useContext, useEffect, useState } from "react";
import { Card, Grid2, Typography, Box, Grid } from "@mui/material";
import AuthContext from "../Context/AuthProvider";
import { getFacilityUsers } from "../API/UsersApi";
import { getClinicalServices } from "../API/FacilitySettingsApi";
import { VisitTypes } from "../Utility/Constants";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  convertToUTC,
  convertUTCToTimeZone,
  convertUTCToTimeZoneDateTimeString,
  convertFacilityTimeToUTC,
} from "../Utility/DateTimeUtility";
import { showLoadingImage } from "../Utility/Utility";
import { useSnackbar } from "../Utility/UserMessageContext";
import MenuBar from "../common/MenuBar";
import GenericDropDown from "../common/GenericDropDown";
import HeaderValueDisplay from "../common/HeaderValueDisplay";
import { getVisitById } from "../API/VisitsApi";
import { getBillByVisitorId } from "../API/BillingApi";
import { Fragment } from "react";
import SchedulingAssistant from "./SchedulingAssistant";
import { addServiceToVisit } from "../API/LinkedServicesApi";
import GenericTextField from "../common/GenericTextField";

// Plugins extend karo
dayjs.extend(utc);
dayjs.extend(timezone);

const AddServicesToVisit = ({
  visitId,
  visitDisplayId,
  closeTab,
  onViewPatientDetails,
  onEditBill,
}) => {
  const { showSnackbar } = useSnackbar();
  const { auth } = useContext(AuthContext);

  // Visit details
  const [visitDetails, setVisitDetails] = useState(null);

  // Service form fields
  const [availableUsers, setAvailableUsers] = useState();
  const [availableServices, setAvailableServices] = useState();
  const [selectedServiceId, setSelectedServiceId] = useState();
  const [selectedUserId, setSelectedUserId] = useState();
  const [selectedService, setSelectedService] = useState(null);
  const [selectedUser, setSelectedUser] = useState();
  const [selectedDateLocal, setSelectedDateLocal] = useState();
  const [selectedFromLocal, setSelectedFromLocal] = useState(null);
  const [selectedToLocal, setSelectedToLocal] = useState(null);

  const [onwardTravelTimeInMins, setOnwardTravelTimeInMins] = useState(0);
  const [returnTravelTimeInMins, setReturnTravelTimeInMins] = useState(0);
  const [addingService, setAddingService] = useState(false);
  const [discountInPercentage, setDiscountInPercentage] = useState(0);
  const [payableAmount, setPayableAmount] = useState(0);

  // Billing state
  const [billDetails, setBillDetails] = useState(null);
  const [billAmountSummary, setBillAmountSummary] = useState({
    totalAmount: 0,
    paidAmount: 0,
    refundedAmount: 0,
    balanceAmount: 0,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadVisitDetails = async () => {
      const result = await getVisitById(
        auth,
        visitId,
        auth?.selectedFacility?.id,
      );
      if (result?.isSuccess) {
        setVisitDetails(result?.data);

        if (result?.data?.scheduledStartDateTime) {
          setSelectedDateLocal(
            convertUTCToTimeZone(
              result?.data?.scheduledStartDateTime,
              auth?.selectedFacility?.timeZone,
            ),
          );
        }
      } else {
        showSnackbar("Failed to load visit details", "error");
      }
    };

    const getUsers = async () => {
      const result = await getFacilityUsers(auth, {
        facilityId: auth.selectedFacility.id,
        roles: ["Physician", "Technician", "Nurse"],
      });
      if (result?.isSuccess) {
        setAvailableUsers(result?.data);
      }
    };

    loadVisitDetails();
    getUsers();
  }, [auth, visitId]);

  useEffect(() => {
    const setClinicalServices = async () => {
      if (!visitDetails) return;

      const visitType = visitDetails?.currentVisitType;

      if (!visitType) {
        showSnackbar("Visit type is missing from visit details", "error");
        return;
      }

      const filters = {
        facilityId: auth.selectedFacility.id,
        visitType: visitType,
      };

      const result = await getClinicalServices(auth, filters);
      if (result.isSuccess) {
        setAvailableServices(result?.data);
      } else {
        showSnackbar(
          "Error occurred while fetching clinical services.",
          "error",
        );
      }
    };

    setClinicalServices();
  }, [auth, visitDetails]);

  useEffect(() => {
    const loadBillingData = async () => {
      if (!visitId) {
        console.warn("No valid Visit ID to fetch billing data.");
        return;
      }

      setLoading(true);
      try {
        const result = await getBillByVisitorId(auth, visitId);

        if (result.isSuccess) {
          const data = result.data;
          setBillDetails({
            billNo: data.displayId || "N/A",
            createdBy: data.createdByUserName || "N/A",
            createdOn: data.createdOn || "N/A",
            billId: data.id || "N/A",
          });

          const updatedSummary = {
            totalAmount: data.totalAmount ?? 0,
            paidAmount: data.paidAmount ?? 0,
            refundedAmount: data.refundedAmount ?? 0,
            balanceAmount: data.balanceAmount ?? 0,
          };
          setBillAmountSummary(updatedSummary);
        } else {
          setBillDetails(null);
        }
      } catch (error) {
        console.error("Error fetching billing details", error);
        setBillDetails(null);
      } finally {
        setLoading(false);
      }
    };

    if (visitId) {
      loadBillingData();
    }
  }, [auth, visitId]);

  useEffect(() => {
    if (!selectedService) return;
    const fees = selectedService.fees;
    if (!discountInPercentage || discountInPercentage === 0) {
      setPayableAmount(fees);
      return;
    }
    const discountedAmount = fees - (fees * discountInPercentage) / 100;
    setPayableAmount(discountedAmount);
  }, [discountInPercentage, selectedService]);

  const addService = async () => {
    setAddingService(true);

    try {
      const visitType = visitDetails?.currentVisitType;

      if (!selectedFromLocal || !selectedToLocal) {
        showSnackbar("Please select both start and end times", "error");
        setAddingService(false);
        return;
      }

      const result = await addServiceToVisit(
        auth,
        visitId,
        selectedServiceId,
        selectedUserId,
        convertToUTC(
          dayjs(selectedDateLocal)
            .set("hour", selectedFromLocal?.hour())
            .set("minute", selectedFromLocal?.minute()),
        ),
        convertToUTC(
          dayjs(selectedDateLocal)
            .set("hour", selectedToLocal?.hour())
            .set("minute", selectedToLocal?.minute()),
        ),
        "",
        visitType,
        discountInPercentage,
      );

      if (result.isSuccess) {
        showSnackbar("Service added successfully to the visit!", "success");
        closeTab(`AddServices-${visitId}`);
      } else {
        showSnackbar(
          result?.data || "Failed to add service to the visit.",
          "error",
        );
      }
    } catch (error) {
      console.error("Error adding service:", error);
      showSnackbar(
        "Error occurred while adding service to the visit.",
        "error",
      );
    } finally {
      setAddingService(false);
    }
  };
  return (
    <Grid2 container>
      {/* Fixed MenuBar Section */}
      <Grid2
        container
        size={12}
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1100,
          backgroundColor: "white",
        }}
      >
        <Grid2 size={12}>
          {addingService && (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(216, 214, 214, 0.4)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 1301,
              }}
            >
              {showLoadingImage()}
            </div>
          )}

          <MenuBar
            showSave={true}
            onSave={() => addService()}
            saveLabel="Add Service"
            saveTooltip="Add Service to Visit"
            disabled={
              addingService ||
              !selectedServiceId ||
              !selectedUserId ||
              !selectedFromLocal ||
              !selectedToLocal
            }
          />
        </Grid2>
      </Grid2>

      {/* Scrollable Content Section */}
      <Grid2
        size={{ xs: 12, sm: 12, md: 12, lg: 9, xl: 9 }}
        container
        spacing={2}
        sx={{
          ml: "5px",
          maxHeight: "calc(100vh - 150px)",
          overflowY: "auto",
          mt: "5px",
        }}
      >
        <Card className="outerCard" variant="outlined">
          <Grid2 size={12} container spacing={2} sx={{ padding: 2 }}>
            {/* Header Section */}
            <Grid2 size={12}>
              <Typography variant="body1" fontWeight="bold" mb={1}>
                Add a new service to a visit: {visitDisplayId} (
                {convertUTCToTimeZoneDateTimeString(
                  visitDetails?.scheduledStartDateTime,
                  auth?.selectedFacility?.timeZone,
                ) || visitDisplayId}
                )
              </Typography>
            </Grid2>

            {/* Patient & Visit Info */}
            <Grid2 container size={12}>
              <Grid2 size={4}>
                <HeaderValueDisplay
                  header="Patient Name"
                  value={
                    <span
                      style={{
                        textDecoration: "underline",
                        cursor: "pointer",
                      }}
                      onClick={() =>
                        onViewPatientDetails &&
                        onViewPatientDetails({
                          id: visitDetails?.patientId,
                          firstName: visitDetails?.patientFirstName,
                          lastName: visitDetails?.patientLastName,
                        })
                      }
                    >
                      {visitDetails?.patientFirstName}{" "}
                      {visitDetails?.patientLastName}
                    </span>
                  }
                />
              </Grid2>
              <Grid2 size={4}>
                <HeaderValueDisplay
                  header="MRN"
                  value={visitDetails?.patientMRN || "N/A"}
                />
              </Grid2>
              <Grid2 size={4}>
                <HeaderValueDisplay
                  header="Bill No."
                  value={
                    billDetails?.billNo ? (
                      <span
                        style={{
                          textDecoration: "underline",
                          cursor: "pointer",
                        }}
                        onClick={() =>
                          onEditBill &&
                          onEditBill({
                            id: billDetails?.billId,
                            displayId: billDetails?.billNo,
                            patientName: `${visitDetails?.patientFirstName} ${visitDetails?.patientLastName}`,
                          })
                        }
                      >
                        {billDetails.billNo}
                      </span>
                    ) : (
                      "N/A"
                    )
                  }
                />
              </Grid2>
            </Grid2>

            {/* Main Content - Two Vertical Sections */}
            <Grid2 size={12} container spacing={3}>
              {/* First Vertical Part: Service and Visit Related Fields */}
              <Grid2 size={6}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: "medium", color: "primary.main" }}
                >
                  Service Details & Scheduling
                </Typography>
                <Grid2 container spacing={2}>
                  <Grid2 size={12}>
                    <GenericDropDown
                      label="Service"
                      value={selectedServiceId || ""}
                      onChange={(e) => {
                        const serviceId = e.target.value;
                        setSelectedServiceId(serviceId);
                        const service = availableServices?.find(
                          (s) => s.id === serviceId,
                        );
                        setSelectedService(service);
                      }}
                      options={
                        availableServices?.map((s) => ({
                          value: s.id,
                          label: `${s.name}, Fees Rs. ${s.fees}, Duration ${s.durationInMins} Mins`,
                        })) || []
                      }
                      required={false}
                      error={false}
                      name="service"
                      disabled={addingService}
                      sx={{
                        "& .MuiPaper-root": {
                          maxHeight: 300,
                          maxWidth: "90vw",
                        },
                        "& .MuiMenuItem-root": {
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        },
                      }}
                    />
                  </Grid2>

                  <Grid2 size={12}>
                    <GenericDropDown
                      label="Assign To"
                      value={selectedUserId || ""}
                      onChange={(e) => {
                        setSelectedUserId(e.target.value);
                        setSelectedUser(
                          availableUsers.find((p) => p.id === e.target.value),
                        );
                      }}
                      options={
                        availableUsers?.map((p) => ({
                          value: p.id,
                          label: `${p.salutation} ${p.firstName} ${p.lastName}${p.suffix ? ", " + p.suffix : ""}`,
                        })) || []
                      }
                      required={false}
                      error={false}
                      name="assignTo"
                      disabled={addingService}
                    />
                  </Grid2>

                  <Grid2 size={6}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <TimePicker
                        label="From"
                        value={dayjs(selectedFromLocal)}
                        disabled={addingService}
                        onChange={(value) => {
                          // if (!value || !selectedDateLocal) return;

                          setSelectedFromLocal(value);
                          //setScheduledStartDateTimeLocal(fromTime);
                          if (selectedService?.durationInMins) {
                            const toTime = value.add(
                              selectedService.durationInMins,
                              "minute",
                            );
                            setSelectedToLocal(toTime);
                          }
                        }}
                        slotProps={{
                          textField: {
                            size: "small",
                            sx: { backgroundColor: "white" },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Grid2>

                  <Grid2 size={6}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <TimePicker
                        label="To"
                        value={dayjs(selectedToLocal)}
                        disabled={addingService}
                        minTime={selectedFromLocal}
                        onChange={(value) => {
                          setSelectedToLocal(value);
                        }}
                        slotProps={{
                          textField: {
                            size: "small",
                            sx: { backgroundColor: "white" },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Grid2>
                  {billDetails?.billNo && (
                    <>
                      <Grid2 size={4}>
                        <GenericTextField
                          label="Discount (%)"
                          fullWidth
                          type="number"
                          value={discountInPercentage}
                          onChange={(e) =>
                            setDiscountInPercentage(e.target.value)
                          }
                          disabled={addingService || !selectedServiceId}
                          placeholder="Enter discount %"
                          size="small"
                          inputProps={{ min: 0, max: 100 }}
                        />
                      </Grid2>
                      <Grid2 size={4}>
                        <GenericTextField
                          label="Original Price"
                          fullWidth
                          type="number"
                          value={selectedService?.fees || 0}
                          size="small"
                          inputProps={{ readOnly: true }}
                        />
                      </Grid2>
                      <Grid2 size={4}>
                        <GenericTextField
                          label="Discounted Amount"
                          fullWidth
                          type="number"
                          value={payableAmount}
                          size="small"
                          inputProps={{ readOnly: true }}
                        />
                      </Grid2>
                    </>
                  )}
                </Grid2>
              </Grid2>

              {/* Second Vertical Part: Scheduling Assistant */}
              <Grid2 size={6}>
                <SchedulingAssistant
                  physician={selectedUser}
                  selectedDate={selectedDateLocal}
                  newSlot={{
                    from: dayjs(selectedDateLocal)
                      .set("hour", selectedFromLocal?.hour())
                      .set("minute", selectedFromLocal?.minute()),
                    to: dayjs(selectedDateLocal)
                      .set("hour", selectedToLocal?.hour())
                      .set("minute", selectedToLocal?.minute()),
                    new: true,
                    title: "New Service",
                    onwardTravelTime: onwardTravelTimeInMins,
                    returnTravelTime: returnTravelTimeInMins,
                  }}
                />
              </Grid2>
            </Grid2>
          </Grid2>
        </Card>
      </Grid2>
    </Grid2>
  );
};

export default AddServicesToVisit;
